/**
 * Shared source grammar for editorial markers used by source extraction,
 * replacement staging, fallback visibility, and the workbench markup helper.
 *
 * Keep these exports as regex *sources*, rather than stateful global RegExp
 * objects. Each consumer can then choose its own flags without sharing
 * `lastIndex`. Existing legacy forms remain first-class compatibility rules;
 * explicit start/end forms are additive and deterministic.
 */
export const SOURCE_TEXT_ARTIFACT_GRAMMAR_VERSION = '2026-09-01.v2'

export const LEGEND_LEGACY_TAG_REGEX_SOURCE = String.raw`\[\s*legend\s*\]`
export const LEGEND_EXPLICIT_START_TAG_REGEX_SOURCE = String.raw`\[\s*legend\s*:\s*start(?:\s+[^\]]*)?\s*\]`
export const LEGEND_EXPLICIT_END_TAG_REGEX_SOURCE = String.raw`\[\s*legend\s*:\s*end\s*\]`

export const LEGEND_EXPLICIT_BLOCK_REGEX_SOURCE = String.raw`${LEGEND_EXPLICIT_START_TAG_REGEX_SOURCE}([\s\S]*?)${LEGEND_EXPLICIT_END_TAG_REGEX_SOURCE}`
export const LEGEND_EXPLICIT_BOUNDARY_TAG_REGEX_SOURCE = String.raw`(?:${LEGEND_EXPLICIT_START_TAG_REGEX_SOURCE}|${LEGEND_EXPLICIT_END_TAG_REGEX_SOURCE})`

export const SIDE_TITLE_LEGACY_TAG_REGEX_SOURCE = String.raw`\[\s*side\s+title(?!\s*:)\s*,?\s*([^\]]*)\]`
export const SIDE_TITLE_LEGACY_TAG_NO_CAPTURE_REGEX_SOURCE = String.raw`\[\s*side\s+title(?!\s*:)\s*,?\s*[^\]]*\]`
export const SIDE_TITLE_EXPLICIT_START_TAG_REGEX_SOURCE = String.raw`\[\s*side(?:\s+|-)title\s*:\s*start(?:\s+([^\]]*))?\s*\]`
export const SIDE_TITLE_EXPLICIT_END_TAG_REGEX_SOURCE = String.raw`\[\s*side(?:\s+|-)title\s*:\s*end\s*\]`

export const PARATEXT_EXPLICIT_START_TAG_REGEX_SOURCE = String.raw`\[\s*paratext\s*:\s*start(?:\s+([^\]]*))?\s*\]`
export const PARATEXT_EXPLICIT_END_TAG_REGEX_SOURCE = String.raw`\[\s*paratext\s*:\s*end\s*\]`

export const APPARATUS_EXPLICIT_START_TAG_REGEX_SOURCE = String.raw`\[\s*apparatus\s*:\s*start(?:\s+([^\]]*))?\s*\]`
export const APPARATUS_EXPLICIT_END_TAG_REGEX_SOURCE = String.raw`\[\s*apparatus\s*:\s*end\s*\]`

export const NOTE_ANCHOR_TAG_REGEX_SOURCE = String.raw`\[\s*note(?:\s+|-)anchor\s*:\s*([0-9\u0660-\u0669\u06F0-\u06F9]+)\s*\]`
export const HAND_CHANGE_TAG_REGEX_SOURCE = String.raw`\[\s*(?:hand(?:\s+|-)change(?:\s+([^\]]*))?|change\s+of\s+hands?|return\s+to\s+(?:the\s+)?previous\s+hand)\s*\]`
export const ILLUSTRATION_TAG_REGEX_SOURCE = String.raw`\[\s*(?:illustration|miniature)\s*\]`
export const BLANK_TAG_REGEX_SOURCE = String.raw`\[\s*blank\s*\]`
export const LACUNA_TAG_REGEX_SOURCE = String.raw`\[\s*lacunae?\s*\]`
export const EXPLICIT_FOLIO_TAG_REGEX_SOURCE = String.raw`\[\s*folio\s*:\s*([^\]]+?)\s*\]`
export const EXPLICIT_DISORDER_TAG_REGEX_SOURCE = String.raw`\[\s*disorder\s*:\s*([^\]]+?)\s*\]`
export const SEGMENTATION_MARKER_REGEX_SOURCE = String.raw`[\u200e\u200f\u202a-\u202e\u2066-\u2069\ufeff]*\/\s*([A-Za-z]{1,5}[–-]\d+(?:[.\-–]+\d+)*)\s*\/[\u200e\u200f\u202a-\u202e\u2066-\u2069\ufeff]*`
export const SEGMENTATION_MARKER_COMPARISON_REGEX_SOURCE = String.raw`[\u200e\u200f\u202a-\u202e\u2066-\u2069\ufeff]*\/?\s*[A-Za-z]{1,5}[–-]\d+(?:[.\-–]+\d+)*\s*\/?[\u200e\u200f\u202a-\u202e\u2066-\u2069\ufeff]*`

export const INLINE_ASTERISK_EDITORIAL_MARKER_REGEX_SOURCE = String.raw`(^|[\s\u00a0])\*(?=[\u0600-\u06ff])`
export const INLINE_DAGGER_EDITORIAL_MARKER_REGEX_SOURCE = String.raw`(^|[\s\u00a0])†(?=[\u0600-\u06ff])`
export const FOOTNOTE_BODY_LINE_REGEX_SOURCE = String.raw`^\s*[*‡§]?\s*([0-9\u0660-\u0669\u06F0-\u06F9]+)[\]).:-]\s+(.+)$`
export const ARABIC_TRAILING_FOOTNOTE_ANCHOR_REGEX_SOURCE = String.raw`([\u0600-\u06FF])[*‡§]?([0-9\u0660-\u0669\u06F0-\u06F9]+)(?=\s|$|[.,;:،؛])`

/** Every controlled bracket marker, including explicit block boundaries. */
export const CONTROLLED_SOURCE_MARKER_REGEX_SOURCE = String.raw`(?:${LEGEND_LEGACY_TAG_REGEX_SOURCE}|${LEGEND_EXPLICIT_START_TAG_REGEX_SOURCE}|${LEGEND_EXPLICIT_END_TAG_REGEX_SOURCE}|${SIDE_TITLE_LEGACY_TAG_REGEX_SOURCE}|${SIDE_TITLE_EXPLICIT_START_TAG_REGEX_SOURCE}|${SIDE_TITLE_EXPLICIT_END_TAG_REGEX_SOURCE}|${PARATEXT_EXPLICIT_START_TAG_REGEX_SOURCE}|${PARATEXT_EXPLICIT_END_TAG_REGEX_SOURCE}|${APPARATUS_EXPLICIT_START_TAG_REGEX_SOURCE}|${APPARATUS_EXPLICIT_END_TAG_REGEX_SOURCE}|${NOTE_ANCHOR_TAG_REGEX_SOURCE}|${HAND_CHANGE_TAG_REGEX_SOURCE}|${ILLUSTRATION_TAG_REGEX_SOURCE}|${BLANK_TAG_REGEX_SOURCE}|${LACUNA_TAG_REGEX_SOURCE}|${EXPLICIT_FOLIO_TAG_REGEX_SOURCE}|${EXPLICIT_DISORDER_TAG_REGEX_SOURCE})`

/**
 * Only controlled markers are hidden in raw/fallback workspace projections.
 * Unknown bracketed material deliberately remains visible for editorial review.
 */
export const HIDDEN_EDITORIAL_MARKER_REGEX_SOURCE = CONTROLLED_SOURCE_MARKER_REGEX_SOURCE

export const SOURCE_MARKUP_TEMPLATES = {
  illustration: '[illustration]',
  blank: '[blank]',
  lacuna: '[lacuna]',
  legend: '[legend:start]\n\n[legend:end]',
  side_title: '[side-title:start placement="left margin"]\n\n[side-title:end]',
  paratext: '[paratext:start type="scribal-note"]\n\n[paratext:end]',
  apparatus: '[apparatus:start]\n\n[apparatus:end]',
  hand_change: '[hand-change certainty="certain"]',
} as const

const MARKER_ATTRIBUTE = /([a-z][a-z0-9_-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s]+))/gi

export const parseSourceMarkerAttributes = (value: string | undefined): Record<string, string> => {
  const attributes: Record<string, string> = {}
  const source = value || ''
  let match: RegExpExecArray | null = MARKER_ATTRIBUTE.exec(source)
  while (match) {
    const key = (match[1] || '').trim().toLowerCase().replace(/-/g, '_')
    const attributeValue = (match[2] ?? match[3] ?? match[4] ?? '').trim()
    if (key && attributeValue) {
      attributes[key] = attributeValue
    }
    match = MARKER_ATTRIBUTE.exec(source)
  }
  MARKER_ATTRIBUTE.lastIndex = 0
  return attributes
}
